import React, { useEffect, useMemo, useRef } from "react";
import "./ClientWelcomeUpdatesModal.css";

export default function ClientWelcomeUpdatesModal({
  open,
  onClose,
  onCta,
  clientName,
}) {
  const dialogRef = useRef(null);

  const content = useMemo(
    () => ({
      title: "Novidades",
      heading: clientName ? `Bem-vindo(a), ${clientName}!` : "Bem-vindo(a)!",
      heroTitle: "Uma nova experiência está pronta",
      intro:
        "Atualizamos a plataforma por completo para deixar tudo mais rápido, mais claro e mais fácil de usar no dia a dia. A nova área do cliente concentra as principais tarefas com um fluxo mais moderno e organizado.",
      testNoticeTitle: "VERSÃO EM TESTE",
      testNoticeText:
        "Você está acessando uma prévia da nova área do cliente. Algumas telas e recursos ainda podem receber ajustes.",
      highlights: [
        {
          title: "Nova Plataforma",
          desc:
            "Interface mais limpa, navegação mais consistente e melhor organização das informações para você encontrar tudo com menos esforço.",
        },
        {
          title: "Newsletter: novas funcionalidades",
          desc:
            "Evoluímos a newsletter para tornar o processo mais prático, com melhorias no fluxo, geração e apresentação dos conteúdos.",
        },
        {
          title: "Geração de PDF aprimorada",
          desc:
            "Exportações mais estáveis e com melhor apresentação, facilitando compartilhar e arquivar materiais com o time.",
        },
        {
          title: "Novo Relatório Analítico",
          desc:
            "Uma visão totalmente personalizável, com liberdade para escolher métricas, períodos e visualizações, permitindo acompanhar resultados e extrair insights de forma muito mais estratégica.",
        },
        {
          title: "Tela de Ajuda",
          desc:
            "Um espaço dedicado para orientações, dúvidas comuns e guias rápidos para acelerar o uso da plataforma.",
        },
        {
          title: "Tema do sistema (claro/escuro)",
          desc:
            "Escolha o tema que prefere para trabalhar com mais conforto, de acordo com o seu ambiente.",
        },
      ],
      footerNote:
        "Se preferir, você pode continuar na tela atual e conhecer a nova página depois.",
    }),
    [clientName]
  );

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="cwu-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="cwu-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Novidades"
        ref={dialogRef}
      >
        <div className="cwu-header">
          <div className="cwu-titleWrap">
            <h2 className="cwu-title">{content.title}</h2>
            <div className="cwu-sub">{content.heading}</div>
          </div>
        </div>

        <div className="cwu-testBanner" role="note" aria-label="Versão em teste">
          <div className="cwu-testBadge" aria-hidden="true">
            <span className="cwu-testDot" />
            {content.testNoticeTitle}
          </div>
          <div className="cwu-testText">{content.testNoticeText}</div>
        </div>

        <div className="cwu-body">
          <div className="cwu-hero">
            <div className="cwu-heroTitle">{content.heroTitle}</div>
            <div className="cwu-heroText">{content.intro}</div>
            <div className="cwu-heroShine" aria-hidden />
          </div>

          <div className="cwu-sectionTitle">O que mudou</div>

          <div className="cwu-cards">
            {content.highlights.map((it, idx) => (
              <div key={idx} className="cwu-card">
                <div className="cwu-cardHead">
                  <span className="cwu-dot" aria-hidden />
                  <div className="cwu-cardTitle">{it.title}</div>
                </div>
                <div className="cwu-cardDesc">{it.desc}</div>
              </div>
            ))}
          </div>

          <div className="cwu-note">{content.footerNote}</div>
        </div>

        <div className="cwu-footer">
          <button className="cwu-ghost" onClick={onClose} type="button">
            Agora não
          </button>

          <button className="cwu-primary" onClick={onCta} type="button">
            Ir para a nova página do cliente
          </button>
        </div>
      </div>
    </div>
  );
}
